import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type MegaMenuItem = {
  id: number;
  label: string;
  subMenus?: {
    title: string;
    items: {
      label: string;
      description: string;
      icon: React.ElementType;
      link?: string;
    }[];
  }[];
  link?: string;
};

export interface MegaMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  items: MegaMenuItem[];
  onItemClick?: (link: string) => void;
  className?: string;
}

const dropdownVariants = {
  hidden: { opacity: 0, y: 8, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const },
  },
  exit: {
    opacity: 0,
    y: 8,
    scale: 0.96,
    transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] as const },
  },
};

const MegaMenu = React.forwardRef<HTMLDivElement, MegaMenuProps>(
  ({ items, onItemClick, className, ...props }, ref) => {
    const [openMenu, setOpenMenu] = React.useState<string | null>(null);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleEnter = (label: string) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setOpenMenu(label);
    };

    const handleLeave = () => {
      timeoutRef.current = setTimeout(() => setOpenMenu(null), 150);
    };

    return (
      <div ref={ref} className={cn("flex shrink-0 items-center gap-0 2xl:gap-1", className)} {...props}>
        {items.map((navItem) => {
          const hasSubmenu = !!navItem.subMenus?.length;
          const isOpen = openMenu === navItem.label;

          return (
            <div
              key={navItem.id}
              className="relative"
              onMouseEnter={() => hasSubmenu ? handleEnter(navItem.label) : undefined}
              onMouseLeave={hasSubmenu ? handleLeave : undefined}
            >
              {/* Top-level label */}
              <button
                onClick={() => {
                  if (navItem.link && onItemClick) {
                    onItemClick(navItem.link);
                  } else if (hasSubmenu) {
                    setOpenMenu(isOpen ? null : navItem.label);
                  }
                }}
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap px-2 2xl:px-3 py-2 text-sm rounded-md transition-colors",
                  "text-muted-foreground hover:text-foreground",
                  isOpen && "text-foreground"
                )}
              >
                {navItem.label}
                {hasSubmenu && (
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform duration-200",
                      isOpen && "rotate-180"
                    )}
                    style={{ color: "#df1212" }}
                  />
                )}
              </button>

              {/* Dropdown */}
              <AnimatePresence>
                {isOpen && hasSubmenu && (
                  <motion.div
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute top-full right-0 pt-3 z-50"
                    onMouseEnter={() => handleEnter(navItem.label)}
                    onMouseLeave={handleLeave}
                  >
                    <div className="rounded-xl border border-border bg-card/95 backdrop-blur-xl shadow-2xl shadow-black/40 p-4 xl:p-5 min-w-[520px] xl:min-w-[600px]">
                      <div
                        className="grid gap-6"
                        style={{
                          gridTemplateColumns: `repeat(${navItem.subMenus!.length}, minmax(0, 1fr))`,
                        }}
                      >
                        {navItem.subMenus!.map((sub) => (
                          <div key={sub.title}>
                            <h4 className="text-[11px] uppercase tracking-[0.15em] font-bold text-white/80 mb-4 border-l-2 border-primary pl-3">
                              {sub.title}
                            </h4>
                            <div className="flex flex-col gap-1">
                              {sub.items.map((item) => {
                                const Icon = item.icon;
                                return (
                                  <button
                                    key={item.label}
                                    onClick={() => {
                                      if (item.link && onItemClick) {
                                        onItemClick(item.link);
                                        setOpenMenu(null);
                                      }
                                    }}
                                    className="flex items-start gap-3 rounded-lg p-2.5 text-left transition-colors hover:bg-secondary/60 group"
                                  >
                                    <div className="mt-0.5 flex-shrink-0 rounded-md bg-primary/10 p-1.5">
                                      <Icon className="h-4 w-4 text-primary" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                                        {item.label}
                                      </p>
                                      <p className="text-xs text-muted-foreground leading-relaxed">
                                        {item.description}
                                      </p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    );
  }
);

MegaMenu.displayName = "MegaMenu";

export default MegaMenu;
